// src/lib/storage/opfs.ts

export type OPFSCapability = 'opfs' | 'indexeddb-fallback' | 'memory-fallback';

let cachedCapability: OPFSCapability | null = null;

// Fix Issue 2 + 3: cache only positive result; probe cleanup in finally block.
export async function detectStorageCapability(): Promise<OPFSCapability> {
  if (cachedCapability === 'opfs') return cachedCapability;
  try {
    if (typeof navigator !== 'undefined' && navigator.storage?.getDirectory) {
      const root = await navigator.storage.getDirectory();
      const testHandle = await root.getFileHandle('.opfs-probe', { create: true });
      try {
        const writable = await testHandle.createWritable();
        await writable.write('ok');
        await writable.close();
      } finally {
        try { await root.removeEntry('.opfs-probe'); } catch { /* best effort */ }
      }
      cachedCapability = 'opfs';
      return cachedCapability;
    }
  } catch {
    // fallthrough — do NOT cache negative results
  }
  if (typeof indexedDB !== 'undefined') return 'indexeddb-fallback';
  return 'memory-fallback';
}

// Allows E2E tests (Task 16) to reset the capability probe.
export function resetCapabilityCache(): void {
  cachedCapability = null;
}

async function getRoot(): Promise<FileSystemDirectoryHandle> {
  return navigator.storage.getDirectory();
}

async function getOrCreateDir(path: string): Promise<FileSystemDirectoryHandle> {
  const parts = path.split('/').filter(Boolean);
  let current = await getRoot();
  for (const part of parts) {
    current = await current.getDirectoryHandle(part, { create: true });
  }
  return current;
}

// Fix Issue 1: read-only directory walker — returns null instead of creating dirs.
async function getDirReadOnly(path: string): Promise<FileSystemDirectoryHandle | null> {
  const parts = path.split('/').filter(Boolean);
  let current = await getRoot();
  for (const part of parts) {
    try {
      current = await current.getDirectoryHandle(part, { create: false });
    } catch {
      return null;
    }
  }
  return current;
}

export async function writeBlob(path: string, blob: Blob): Promise<void> {
  const cap = await detectStorageCapability();
  if (cap !== 'opfs') throw new Error('OPFS not available');
  const parts = path.split('/');
  const fileName = parts.pop()!;
  const dirPath = parts.join('/');
  const dir = await getOrCreateDir(dirPath);
  const fileHandle = await dir.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

export async function readBlob(path: string): Promise<Blob | null> {
  const cap = await detectStorageCapability();
  if (cap !== 'opfs') return null;
  try {
    const parts = path.split('/');
    const fileName = parts.pop()!;
    const dirPath = parts.join('/');
    // Fix Issue 1: use read-only helper so we don't create empty dirs.
    const dir = await getDirReadOnly(dirPath);
    if (!dir) return null;
    const fileHandle = await dir.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return file;
  } catch {
    return null;
  }
}

export async function writeJSON(path: string, data: unknown): Promise<void> {
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
  return writeBlob(path, blob);
}

// Fix Minor Issue 8: log parse errors so they're distinguishable from "file not found".
export async function readJSON<T>(path: string): Promise<T | null> {
  const blob = await readBlob(path);
  if (!blob) return null;
  try {
    return JSON.parse(await blob.text()) as T;
  } catch (e) {
    console.warn(`[opfs] Failed to parse JSON at ${path}:`, e);
    return null;
  }
}

export async function deleteFolder(path: string): Promise<void> {
  const cap = await detectStorageCapability();
  if (cap !== 'opfs') return;
  const parts = path.split('/').filter(Boolean);
  const lastPart = parts.pop()!;
  const parentPath = parts.join('/');
  // Fix Issue 1: use read-only helper — if parent doesn't exist, nothing to delete.
  const parent = parentPath ? await getDirReadOnly(parentPath) : await getRoot();
  if (!parent) return;
  try {
    await parent.removeEntry(lastPart, { recursive: true });
  } catch {
    // already gone
  }
}

export async function listFolders(path: string): Promise<string[]> {
  const cap = await detectStorageCapability();
  if (cap !== 'opfs') return [];
  try {
    // Fix Issue 1: use read-only helper so we don't create the directory chain.
    const dir = await getDirReadOnly(path);
    if (!dir) return [];
    const folders: string[] = [];
    for await (const [name, handle] of (dir as any).entries() as AsyncIterable<[string, FileSystemHandle]>) {
      if (handle.kind === 'directory') folders.push(name);
    }
    return folders;
  } catch {
    return [];
  }
}

export async function estimateQuota(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota };
}
